import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ContractsService } from '../../../services/contracts.service';
import { ErrorService } from '../../../shared/services/error.service';
import { TableAction } from '../../../shared/components/table/table.component';
import { B2B_EXPORT_PAGE_SIZE, exportB2bTableToExcel, rowsFromPagedResponse } from '../../../utils/b2b-table-export.util';

@Component({
  selector: 'app-b2b-contracts-items',
  templateUrl: './b2b-contracts-items.component.html',
  styleUrls: ['./b2b-contracts-items.component.css']
})
export class B2bContractsItemsComponent implements OnInit {
  items: any[] = [];
  isLoading = false;
  contractId: number | null = null;
  contractNumber: string = '';
  contractName: string = '';

  // Paginação (backend)
  pageNumber = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  searchTerm = '';

  // Flag para controlar se já tentou carregar
  hasLoaded = false;

  // Configuração da tabela de pedidos (itens do contrato)
  columns = [
    { titulo: 'Número do Pedido', label: 'itemNumber' },
    { titulo: 'Tipo de Moto', label: 'motorcycleType' },
    { titulo: 'Quantidade', label: 'quantity' },
    { titulo: 'Valor Unitário', label: 'unitValue', formatter: (value: any) => this.formatCurrency(value), sensitive: true },
    { titulo: 'Tipo de Pagamento', label: 'paymentType' },
    { titulo: 'Status', label: 'status', formatter: (value: any) => this.getStatusLabel(value) },
  ];

  actions: TableAction[] = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    },
    {
      label: 'Editar',
      icon: 'fas fa-edit',
      action: 'edit',
      class: 'btn-edit'
    }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contractsService: ContractsService,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    // eslint-disable-next-line no-console
    // eslint-disable-next-line no-console
    
    this.route.params.subscribe(params => {
      // eslint-disable-next-line no-console
      const id = params['id'];
      if (id) {
        this.contractId = +id;
        // eslint-disable-next-line no-console
        if (this.contractId && !isNaN(this.contractId)) {
          // eslint-disable-next-line no-console
          this.loadItems();
          this.loadContractInfo();
        } else {
          // eslint-disable-next-line no-console
          console.error('[B2bContractsItemsComponent] ID do contrato inválido:', id, 'Convertido para:', this.contractId);
          this.errorService.showErrorMessage('ID do contrato inválido');
          this.navigateBack();
        }
      } else {
        // eslint-disable-next-line no-console
        console.error('[B2bContractsItemsComponent] ID do contrato não encontrado na rota. Parâmetros:', params);
        this.errorService.showErrorMessage('ID do contrato não encontrado');
        this.navigateBack();
      }
    });
    
    // Verificar também os query params
    this.route.queryParams.subscribe(queryParams => {
      // eslint-disable-next-line no-console
    });
  }

  loadContractInfo(): void {
    if (!this.contractId) return;

    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (contract) => {
        this.contractNumber = contract.contractNumber || '';
        this.contractName = contract.contractName || '';
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
      }
    });
  }

  loadItems(): void {
    if (!this.contractId) {
      // eslint-disable-next-line no-console
      console.error('[B2bContractsItemsComponent] loadItems: contractId não definido');
      return;
    }

    this.isLoading = true;
    // eslint-disable-next-line no-console

    this.contractsService.getB2bContractItems(this.contractId, this.pageNumber, this.pageSize, this.searchTerm).subscribe({
      next: (response) => {
        // eslint-disable-next-line no-console
        
        // O serviço já retorna { data: [...], pagination: {...} }
        if (response && response.data && Array.isArray(response.data)) {
          this.items = response.data;
          // eslint-disable-next-line no-console
        } else if (Array.isArray(response)) {
          // Fallback: se a resposta for um array direto (não deveria acontecer, mas por segurança)
          this.items = response;
          // eslint-disable-next-line no-console
        } else {
          this.items = [];
        }

        // Atualizar informações de paginação
        if (response && response.pagination) {
          this.totalItems = response.pagination.totalItems || 0;
          this.totalPages = response.pagination.totalPages || Math.max(1, Math.ceil(this.totalItems / Math.max(1, this.pageSize)));
          this.pageNumber = response.pagination.currentPage || this.pageNumber;
        } else {
          this.totalItems = this.items.length;
          this.totalPages = 1;
        }
        
        // eslint-disable-next-line no-console
        // eslint-disable-next-line no-console
        // eslint-disable-next-line no-console
        this.hasLoaded = true;
        this.isLoading = false;
      },
      error: (err) => {
        // eslint-disable-next-line no-console
        console.error('[B2bContractsItemsComponent] Erro ao carregar itens:', err);
        // eslint-disable-next-line no-console
        console.error('[B2bContractsItemsComponent] Detalhes do erro:', {
          status: err?.status,
          statusText: err?.statusText,
          message: err?.message,
          error: err?.error,
          url: err?.url
        });
        
        // Não redirecionar automaticamente, apenas mostrar erro e manter na página
        if (err?.status === 404) {
          this.errorService.showErrorMessage('Itens não encontrados para este contrato.');
        } else {
          this.errorService.handleHttpError(err);
        }
        
        this.hasLoaded = true;
        this.isLoading = false;
        this.items = [];
      }
    });
  }

  onSearchChange(term: string): void {
    this.searchTerm = (term || '').trim();
    this.pageNumber = 1;
    this.loadItems();
  }

  onPageChange(page: number): void {
    if (!page || page === this.pageNumber) return;
    this.pageNumber = page;
    this.loadItems();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size > 0 ? size : 10;
    this.pageNumber = 1;
    this.loadItems();
  }

  onActionClick(actionData: { action: string; item: any }): void {
    const { action, item } = actionData;

    switch (action) {
      case 'view':
        this.viewItem(item.id);
        break;
      case 'edit':
        this.editItem(item.id);
        break;
      default:
        // eslint-disable-next-line no-console
    }
  }

  viewItem(itemId: number): void {
    if (!this.contractId) return;
    this.router.navigate(['/b2b/contracts', this.contractId, 'items', itemId, 'view']);
  }

  editItem(itemId: number): void {
    if (!this.contractId) return;
    this.router.navigate(['/b2b/contracts', this.contractId, 'items', itemId, 'edit']);
  }

  newItem(): void {
    if (!this.contractId) return;
    this.router.navigate(['/b2b/contracts', this.contractId, 'items', 'new']);
  }

  exportExcel(): void {
    if (!this.contractId) return;
    const size = Math.max(this.totalItems || 0, B2B_EXPORT_PAGE_SIZE);
    this.contractsService.getB2bContractItems(this.contractId, 1, size, this.searchTerm).subscribe({
      next: (response) => {
        exportB2bTableToExcel(
          `b2b-pedidos-${this.contractNumber || this.contractId}`,
          this.columns,
          rowsFromPagedResponse(response),
          'Pedidos'
        );
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  navigateBack(): void {
    if (this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
    } else {
      this.router.navigate(['/b2b/contracts']);
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  }

  formatCurrency(value: number): string {
    if (!value && value !== 0) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  getStatusLabel(status: string): string {
    const statusMap: { [key: string]: string } = {
      'Active': 'Ativo',
      'Inactive': 'Inativo',
      'Pending': 'Pendente'
    };
    return statusMap[status] || status || 'N/A';
  }
}
